/*
 * Functions to ease mpd dash manifest parsing.
 *
 * This is a naiv approach to parse and get only what we need at the
 * time of writing. So there are a lot more node types and attributes
 * than the types provided.
 *
 * This is explicitly not designed to cover the full spec.
 */

//
// Types
//

export type NodeTagName = 'Period' | 'AdaptationSet' | 'Label' | 'BaseURL';
export type NodeAttribute = 'contentType' | 'lang' | 'id' | 'mimeType';
export type NodeContentType = 'audio' | 'text' | 'video';

//
// Dom Traversal API
//

const fromString = (xmlManifest: string): Document | undefined => {
  const domParser = new DOMParser();
  const dom = domParser.parseFromString(xmlManifest, 'application/xml');
  const ret = dom.getElementsByTagName('MPD');
  return ret.length > 0 ? dom : undefined;
};

const getAllNodes = (tagName: NodeTagName, node: Document | Element): Element[] => {
  const nodeList = node.getElementsByTagName(tagName);
  return Array.from(nodeList);
};

const getFirstNode = (tagName: NodeTagName, node: Document | Element): Element | undefined =>
  getAllNodes(tagName, node)[0];

const getAttribute = (node: Element, attribute: NodeAttribute): string | undefined =>
  node.getAttribute(attribute) ?? undefined;

/**
 * Returns a list of attributes from given node in entries format, a
 * list of tuples with the name and value:
 * [
 *    [ attributeName, attributeValue ],
 *    [ attributeName, attributeValue ],
 * ]
 *
 * To transform this list to an object you can use Object.fromEntries()
 */
const getAttributes = <const Attr extends NodeAttribute>(node: Element, attrs: Attr[]) =>
  attrs
    .map((value): [NodeAttribute, string | undefined] => [value, getAttribute(node, value)])
    .filter(<T>(param: [NodeAttribute, T | undefined]): param is [NodeAttribute, T] => param[1] !== undefined);

const getText = (node: Document | Element): string => node.textContent ?? '';

/**
 * Returns a list with a tuple of the given node type and the value of the attribute:
 *
 * Example:
 * [
 *   [ NodeTagName, string ],
 *   [ NodeTagName, string ],
 *   [ NodeTagName, string ],
 *
 *   // ....
 * ]
 */
const getAllNodesWithAttribute = ({
  tagName,
  attribute,
  node,
}: {
  tagName: NodeTagName;
  attribute: NodeAttribute;
  node: Document | Element;
}): Array<[Element, string | undefined]> =>
  getAllNodes(tagName, node).map((node) => [node, getAttribute(node, attribute)]);

//
// MPD Specific API
//

const getAllAdaptationSetsWithContentType = (node: Document | Element, contentType: NodeContentType) =>
  // We don't use querySelectorAll(`AdaptionSet[contentType="${contentType}"]`),
  // because it's slower. In the magnitude of 1ms vs 500ms.
  getAllNodesWithAttribute({ tagName: 'AdaptationSet', attribute: 'contentType', node })
    .filter(([_, value]) => value === contentType)
    .map(([node, _]) => node);

const getAllAudioTracks = (node: Document | Element) => getAllAdaptationSetsWithContentType(node, 'audio');
const getAllTextTracks = (node: Document | Element) => getAllAdaptationSetsWithContentType(node, 'text');

const getLanguageCode = (node: Element) => getAttribute(node, 'lang') ?? '';

const getLanguageLabel = (node: Element): [string, string] | undefined => {
  const mimeType = getAttribute(node, 'mimeType') || '';
  const id = getAttribute(node, 'id') || '';
  const labelNode = getFirstNode('Label', node);
  const label = labelNode !== undefined ? getText(labelNode) : '';
  // We have to construct the id ourselves because
  // the bitmovin player does not use the ID of the audio track but
  // a combination of the mimeType and the id.
  return buildBitmovinAudioTrackId(id, mimeType, label);
};

const buildBitmovinAudioTrackId = (id: string, mimeType: string, label: string): [string, string] | undefined => {
  return mimeType !== '' && id !== '' ? [`${mimeType}-${id}`, label] : undefined;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export const MPDParser = {
  fromString,
  getAllNodes,
  getFirstNode,
  getAttribute,
  getAttributes,
  getText,
  getAllNodesWithAttribute,
  getAllAdaptationSetsWithContentType,
  getAllAudioTracks,
  getAllTextTracks,
  getLanguageCode,
  getLanguageLabel,
} as const;
